import { API_ENDPOINTS } from '../config/api';
import { supabase } from '../lib/supabase';

interface RecordingResult {
  id: string;
  audioUrl: string;
  userId: string;
  createdAt: string;
}

interface Post {
  id: string;
  userId: string;
  recordingId: string;
  content: string;
  editedContent: string | null;
  status: 'draft' | 'published';
  createdAt: string;
  updatedAt: string;
  analysisId: string;
}

export async function transcribeAudio(blob: Blob): Promise<{ text: string }> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('No active session');
    }

    const formData = new FormData();
    formData.append('file', blob, 'audio.wav');
    
    const response = await fetch(API_ENDPOINTS.TRANSCRIBE_AUDIO, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'x-user-id': session.user.id
      },
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Transcription API error:', errorText);
      throw new Error(errorText);
    }

    const responseText = await response.text();
    let data = JSON.parse(responseText);

    // Handle empty transcription
    if (!data.text || data.text.trim().length === 0) {
      throw new Error('No speech detected in the recording. Please try again.');
    }

    return { text: data.text };
  } catch (error) {
    console.error('Error transcribing audio:', error);
    throw error;
  }
}

export async function generateThoughtLeadershipPost(transcript: string) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('No active session');
    }

    const requestData = {
      transcript,
      userId: session.user.id
    };

    const response = await fetch(API_ENDPOINTS.GENERATE_POST, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'x-user-id': session.user.id
      },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error response text:', errorText);
      
      let error;
      try {
        error = JSON.parse(errorText);
      } catch {
        error = { message: errorText || 'Unknown error' };
      }
      
      throw new Error(`API error: ${error.message || 'Unknown error'}`);
    }

    const data = await response.json();
    return data.content;
  } catch (error) {
    console.error('Error generating post:', error);
    throw error;
  }
}

export async function saveRecording(blob: Blob, userId: string): Promise<RecordingResult> {
  try {
    // Get the current user's session
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('No active session');
    }

    const formData = new FormData();
    const file = new File([blob], 'recording.webm', { type: 'audio/webm' });
    formData.append('file', file);
    formData.append('user_id', userId);

    const response = await fetch(API_ENDPOINTS.SAVE_RECORDING, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'x-user-id': session.user.id
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(`API error: ${error.message || 'Unknown error'}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error saving recording:', error);
    throw error;
  }
}

export async function updateRecordingTranscript(recordingId: string, transcript: string) {
  try {
    // Get the current user's session
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('No active session');
    }

    const response = await fetch(API_ENDPOINTS.UPDATE_TRANSCRIPT.replace(':id', recordingId), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'x-user-id': session.user.id
      },
      body: JSON.stringify({ transcript }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(`API error: ${error.message || 'Unknown error'}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating transcript:', error);
    throw error;
  }
}

export async function savePost(recordingId: string, content: string, analysisId: string): Promise<Post> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('No active session');
    }

    const { data, error } = await supabase
      .from('posts')
      .insert({
        recording_id: recordingId,
        user_id: session.user.id,
        content,
        status: 'draft',
        analysis_id: analysisId
      })
      .select()
      .single();

    if (error) {
      throw error;
    }

    return {
      id: data.id,
      userId: data.user_id,
      recordingId: data.recording_id,
      content: data.content,
      editedContent: data.edited_content,
      status: data.status,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
      analysisId: analysisId
    };
  } catch (error) {
    console.error('Error saving post:', error);
    throw error;
  }
}